import { FC } from 'react'
import { TabBarProps } from './TabBar.types'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'

const TabBar: FC<TabBarProps> = ({
  tabs,
  activeTab,
  onChange,
  variant = 'default',
  size = 'md',
  className,
  useHeadings = false,
}) => {
  const { t } = useTranslation()

  const containerStyles = clsx(
    'flex flex-row',
    {
      'gap-0.5': variant === 'default',
      'gap-2': variant === 'pills',
      'border-b border-[var(--tab-border)]': variant === 'underline',
      'w-full': true,
      'sm:w-fit': true,
    },
    variant === 'default' &&
      'bg-[var(--tab-bg)] border border-[var(--tab-border)] rounded-lg overflow-hidden',
    className,
  )

  // Mobile grid container for ticket selection tabs
  const mobileGridContainerStyles = clsx(
    'grid grid-cols-2 gap-2 w-full sm:hidden',
    className,
  )

  const tabStyles = clsx(
    'flex items-center justify-center gap-2 cursor-pointer text-sm transition-all duration-200',
    {
      // Size variants with responsive padding
      'px-1 sm:px-4 py-3': size === 'sm',
      'px-1 sm:px-6 py-3': size === 'md',
      'px-2 sm:px-8 py-3': size === 'lg',

      // Gap between icon and text
      'gap-1 sm:gap-2': true,

      // Width handling
      'flex-1': true,
      'sm:flex-initial': true,

      // Variant specific styles
      'first:rounded-l-lg last:rounded-r-lg': variant === 'default',
      'rounded-md': variant === 'pills',
      'border-b-2 -mb-px': variant === 'underline',
    },
  )

  // Mobile grid tab styles for ticket selection
  const mobileGridTabStyles = clsx(
    'flex flex-row items-center justify-start gap-2 cursor-pointer text-sm transition-all duration-200 p-3 rounded-lg border',
    {
      'active-gradient text-[var(--tab-text-active)] font-semibold': false, // Will be set dynamically
      'bg-[var(--tab-bg)] text-[var(--tab-text)] border-[var(--tab-border)] hover:bg-[var(--tab-hover-bg)] hover:text-[var(--tab-hover-text)] hover:border-[var(--tab-hover-border)]': true,
    },
  )

  const getTabStateStyles = (isActive: boolean) => {
    if (variant === 'underline') {
      return isActive
        ? 'border-[var(--tab-active-border)] text-[var(--tab-text-active)] font-semibold'
        : 'border-transparent text-[var(--tab-text)] hover:text-[var(--tab-hover-text)] hover:border-[var(--tab-hover-border)]'
    }

    return isActive
      ? 'active-gradient text-[var(--tab-text-active)] font-semibold'
      : 'text-[var(--tab-text)] hover:bg-[var(--tab-hover-bg)] hover:text-[var(--tab-hover-text)] hover:border-[var(--tab-hover-border)]'
  }

  const tabStateStyle = 'rounded-[var(--radius-none)]'

  const renderLabel = (label: string, isActive: boolean) => {
    // Use translation keys for both desktop and mobile labels
    const mobileKey = label.replace('poll.', 'poll.mobile.')
    const mobileLabel = t(mobileKey, { defaultValue: t(label).split(' ')[0] })
    const desktopLabel = t(label)

    if (!useHeadings) {
      return (
        <>
          <span className="hidden sm:inline text-sm">{desktopLabel}</span>
          <span className="sm:hidden text-xs whitespace-nowrap">
            {mobileLabel}
          </span>
        </>
      )
    }

    return (
      <h4 className={clsx('font-medium m-0', isActive && 'font-semibold')}>
        <>
          <span className="hidden sm:inline text-sm">{desktopLabel}</span>
          <span className="sm:hidden text-xs whitespace-nowrap">
            {mobileLabel}
          </span>
        </>
      </h4>
    )
  }

  return (
    <>
      {/* Mobile Grid Layout - Hidden on desktop */}
      <div className={mobileGridContainerStyles} data-group="tab">
        {tabs.map(({ label, value, icon, disabled }) => {
          const isActive = activeTab === value
          return (
            <div
              key={value}
              className={clsx(
                mobileGridTabStyles,
                isActive
                  ? 'active-gradient text-[var(--tab-text-active)] font-semibold'
                  : 'bg-[var(--tab-bg)] text-[var(--tab-text)] border-[var(--tab-border)] hover:bg-[var(--tab-hover-bg)] hover:text-[var(--tab-hover-text)] hover:border-[var(--tab-hover-border)]',
                disabled && 'opacity-50 cursor-not-allowed',
              )}
              onClick={() => !disabled && onChange(value)}
            >
              {icon && (
                <span
                  className={clsx(
                    'text-lg',
                    isActive
                      ? 'text-[var(--tab-text-active)] opacity-100'
                      : 'opacity-70',
                  )}
                >
                  {icon}
                </span>
              )}
              <span className="text-xs text-center">
                {renderLabel(label, isActive)}
              </span>
            </div>
          )
        })}
      </div>

      {/* Desktop Horizontal Layout - Hidden on mobile */}
      <div className={clsx(containerStyles, 'hidden sm:flex')} data-group="tab">
        {tabs.map(({ label, value, icon, disabled }) => {
          const isActive = activeTab === value
          return (
            <div
              key={value}
              className={clsx(
                tabStyles,
                tabStateStyle,
                getTabStateStyles(isActive),
                disabled && 'opacity-50 cursor-not-allowed',
              )}
              onClick={() => !disabled && onChange(value)}
            >
              {icon && (
                <span
                  className={clsx(
                    'text-base',
                    isActive ? 'opacity-100' : 'opacity-70',
                  )}
                >
                  {icon}
                </span>
              )}
              {renderLabel(label, isActive)}
            </div>
          )
        })}
      </div>
    </>
  )
}

export default TabBar
